import React, { useCallback, useContext, useRef, useState } from 'react'

import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from '../Form/Input/TextInput'
import FoldableContent from '../FoldableContent/FoldableContent'
import styled from 'styled-components'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ContactContext from 'contexts/contactContext'
import { redeemGiftCard } from 'api/promo'
import { validateAndFetchGiftCardBalance } from 'api/payment'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import AnnotatedGiftCard from './AnnotatedGiftCard'
import { RedeemedCard } from './common'
import DividerWithText from '../DividerWithText'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import useToggle from 'hooks/useToggle'
import ScanCode from './ScanCode'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useStateWithRef from 'hooks/useStateWithRef'
import { rem } from 'polished'
import * as Analytics from 'analytics/analytics'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import { isBlackhwakCard } from 'components/Common/RedeemGiftCard/utils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { setUsingGiftCard } from 'actions/CheckoutActions'
import HelpSection from 'checkout/Components/Shared/CustomerServiceInformation/HelpSection'
import config from 'constants/config'

type RedeemToGiftCardResult = {
  cardNumber?: string
  amount: number
  currency: string
  expiresAt?: string
}

type RedeemToCreditsResult = {
  currency: string
  amount: string
}

const StyledGiftGardImage = styled(AnnotatedGiftCard)`
  margin: 0 auto;
  width: 100%;
`

const ScanGiftCardButton = styled(ClickableListItem)`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ErrorExampleList = styled.ul`
  margin-top: ${rem(12)};
  margin-bottom: 0;
`

const StyledHelpSection = {
  textAlign: 'center' as const,
  display: 'flex' as const,
  flexDirection: 'column' as const,
  alignItems: 'center' as const,
  padding: '16px',
}

const ErrorBanner = styled(MessageBanner)`
  margin-top: ${rem(24)};
`

interface Props {
  onRedeem: (redeemedCard: RedeemedCard) => void
  onBack?: () => void
  enableScan?: boolean
}

function EnterCode({ onRedeem, onBack: moveToSummary, enableScan }: Props) {
  const [code, setCode, codeRef] = useStateWithRef('')
  const [pin, setPin, pinRef] = useStateWithRef('')
  const [processing, setProcessing] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('Please check and try again.')
  const { value: isScanMode, on: selectScanMode, off: deselectScanMode } = useToggle()
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [didScanCard, setDidScanCard] = useState(false)
  const didEditManually = useRef(false)

  const { currentRegionCode } = useContext(GeoContext)
  const { defaultContact } = useContext(ContactContext)
  const analyticsPage = useContext(AnalyticsPageContext)

  const shouldAskPin = isBlackhwakCard(code)
  const showSupportNumber = failedAttempts > 2

  const dispatch = useAppDispatch()

  const isGiftCardV2 = config.ENABLE_GIFT_CARD_V2
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)

  const onCodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
    didEditManually.current = true
  }, [setCode])

  const onPinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPin(e.target.value)
    didEditManually.current = true
  }, [setPin])

  const onSubmit = useCallback(async(e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProcessing(true)
    setHasError(false)

    try {
      let redeemResult: RedeemToGiftCardResult | RedeemToCreditsResult
      const sanitisedCode = codeRef.current.toUpperCase().replace(/[^0-9A-Z]/g, '')
      if (isGiftCardV2 && !isSpoofed) {
        const redeemResponse = await validateAndFetchGiftCardBalance(sanitisedCode, currentRegionCode, pinRef.current)
        redeemResult = redeemResponse.result
      } else {
        const redeemResponse = await redeemGiftCard(codeRef.current, pinRef.current, currentRegionCode)
        redeemResult = redeemResponse.result.credits
      }

      if (didScanCard) {
        if (didEditManually.current) {
          Analytics.trackClientEvent({
            subject: 'scan_gift_card',
            action: 'success_modified_from_scan',
            category: analyticsPage,
            type: 'nonInteraction',
          })
        } else {
          Analytics.trackClientEvent({
            subject: 'scan_gift_card',
            action: 'success_exact_match',
            category: analyticsPage,
            type: 'nonInteraction',
          })
        }
      }

      dispatch(setUsingGiftCard(true))

      onRedeem({
        code: sanitisedCode,
        currencyCode: redeemResult.currency,
        amount: parseFloat(String(redeemResult.amount)),
        ...(pinRef.current ? { pin: pinRef.current } : {}),
      })

      if (!isGiftCardV2) {
        const displayCreditAmount = formatDynamicAmountWithDollarType(
          parseFloat(String(redeemResult.amount)),
          redeemResult.currency,
          currentRegionCode,
        )

        showSnackbar(
          `${displayCreditAmount} credit has been applied to your account`,
          'success',
          { heading: 'Gift card redeemed' },
        )
      }
    } catch(e: any) {
      setHasError(true)
      if (e.status === 400) { // display api error message for 400 status
        setErrorMessage(e.message)
      } else if (e.message) { // handle validation errors from validateGiftCard
        setErrorMessage(e.message)
      }
      setFailedAttempts(n => n + 1)
    } finally {
      setProcessing(false)
    }
  }, [codeRef, pinRef, didScanCard, onRedeem, currentRegionCode, analyticsPage, dispatch, isGiftCardV2, isSpoofed])

  const onSelectScanMode = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'scan_gift_card',
      action: 'start',
      category: analyticsPage,
      type: 'interaction',
    })
    selectScanMode()
    setHasError(false)
  }, [selectScanMode, analyticsPage])

  const onScanSuccess = useCallback((code: string, pin: string) => {
    setCode(code)
    setPin(pin)
    setDidScanCard(true)
    didEditManually.current = false
    deselectScanMode()
  }, [deselectScanMode, setCode, setPin])

  if (isScanMode) {
    return <ScanCode onSuccess={onScanSuccess} onCancel={deselectScanMode} />
  }

  return (
    <form onSubmit={onSubmit}>
      <ModalHeader title="Add a Gift Card" onBackButtonClick={moveToSummary} />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText variant="large" as="p">
              Enter the code from your printed voucher, email, or the back of your physical gift card.
            </BodyText>
            <TextInput
              label="Gift card code"
              placeholder="Enter code or gift card number here"
              value={code}
              onChange={onCodeChange}
              disabled={processing}
              manualError
              required
              data-testid="code-field"
            />

            {shouldAskPin &&
              <TextInput
                label={
                  <LabelGroup>
                    {'Access code '}
                    <Tooltip description="Enter the access code that is located in the scratch panel on the back of your physical gift card.">
                      <LineInfoCircleIcon size="XS" />
                    </Tooltip>
                  </LabelGroup>
                }
                placeholder="e.g. XRNJU13RJL"
                value={pin}
                onChange={onPinChange}
                disabled={processing}
                required
                data-testid="pin-field"
              />}
          </VerticalSpacer>
          <>
            <FoldableContent
              openLabel="Where are my card details?"
              closedLabel="Where are my card details?"
              size="medium"
            >
              <BodyText variant="medium">
                For physical cards, the card number is located on the back of your card.
              </BodyText>
              <br />

              <StyledGiftGardImage />

            </FoldableContent>

            {enableScan && <VerticalSpacer gap={16}>
              <DividerWithText text="or" />

              <ScanGiftCardButton
                title="Scan gift card"
                size="large"
                startIcon={<LineCameraIcon />}
                endIcon={<LineAngleRightBIcon />}
                onClick={onSelectScanMode}
              />
            </VerticalSpacer>}
          </>
          {hasError && (
            <VerticalSpacer gap={16}>
              <ErrorBanner kind="critical" heading="Unable to redeem">
                <VerticalSpacer gap={12}>
                  {!didScanCard && !showSupportNumber && errorMessage}
                  {didScanCard && <>
                    <div>Sometimes the card scanner makes mistakes, such as confusing these characters:</div>
                    <ErrorExampleList>
                      <li>8 and B</li>
                      <li>1 and I</li>
                      <li>Q, G and 6</li>
                    </ErrorExampleList>
                    <div>Please check the details carefully and try again.</div>
                    {showSupportNumber && <div>
                      If you're still having trouble, call us on {defaultContact.humanReadable}.
                    </div>}
                  </>}
                </VerticalSpacer>
              </ErrorBanner>
              <div style={StyledHelpSection}>
                <HelpSection contactNumber={defaultContact} />
              </div>
            </VerticalSpacer>
          )}

        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          type: 'submit',
          children: 'Redeem',
          disabled: processing,
        }}
      />
    </form>
  )
}

export default EnterCode
